import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { authApi } from "@/services/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function LoginPage() {
  const navigate = useNavigate();
  const { login, backupLogin, loading, error } = useAdminAuth();
  const [isEmergencyMode, setIsEmergencyMode] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState(null); // null, 'loading', 'sent', 'error'
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    username: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let success;
    if (isEmergencyMode) {
      success = await backupLogin(formData.username, formData.password);
    } else {
      success = await login(formData.email, formData.password);
    }
    if (success) {
      navigate("/dashboard");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotStatus("loading");
    try {
      await authApi.requestPasswordReset(forgotEmail);
      setForgotStatus("sent");
    } catch {
      // Always show success to prevent email enumeration
      setForgotStatus("sent");
    }
  };

  // Forgot password mode
  if (isForgotMode) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-earth-neutral">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-4 pb-2">
            <div className="flex justify-center">
              <img src="/impleo-logo.svg" alt="Impleo" className="h-10 w-auto" />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                Reset Password
              </CardTitle>
              <CardDescription className="text-center">
                {forgotStatus === "sent"
                  ? "If an account exists with that email, a reset link has been sent."
                  : "Enter your email to receive a password reset link."}
              </CardDescription>
            </div>
          </CardHeader>
          {forgotStatus === "sent" ? (
            <CardContent className="pt-2 pb-6 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Check your inbox and follow the link to reset your password.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setIsForgotMode(false);
                  setForgotStatus(null);
                  setForgotEmail("");
                }}
              >
                Back to Sign In
              </Button>
            </CardContent>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={forgotStatus === "loading"}
                >
                  {forgotStatus === "loading" ? "Sending..." : "Send Reset Link"}
                </Button>
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                  onClick={() => {
                    setIsForgotMode(false);
                    setForgotStatus(null);
                    setForgotEmail("");
                  }}
                >
                  Back to Sign In
                </button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-earth-neutral">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4 pb-2">
          {/* Logo */}
          <div className="flex justify-center">
            <img
              src="/impleo-logo.svg"
              alt="Impleo"
              className="h-10 w-auto"
            />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              {isEmergencyMode ? "Emergency Login" : "Admin Portal"}
            </CardTitle>
            <CardDescription className="text-center">
              {isEmergencyMode
                ? "Use backup credentials for emergency access"
                : "Sign in to manage Impleo AI platform"}
            </CardDescription>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {isEmergencyMode ? (
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="admin@impleo.ai"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            <div className="flex w-full flex-col items-center gap-2 text-sm">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground underline"
                onClick={() => setIsEmergencyMode(!isEmergencyMode)}
              >
                {isEmergencyMode
                  ? "Use regular login"
                  : "Emergency login (backup credentials)"}
              </button>

              {!isEmergencyMode && (
                <>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground underline"
                    onClick={() => setIsForgotMode(true)}
                  >
                    Forgot password?
                  </button>
                  <Link
                    to="/signup"
                    className="text-muted-foreground hover:text-foreground underline"
                  >
                    Request admin access
                  </Link>
                </>
              )}
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

export default LoginPage;
